import { fetchShortPaySummary, listOpenDeductions } from "@/api/accounting.tsx"
import { fetchDeliveries, fetchInboundFillRate } from "@/api/inbound_po_line.tsx"
import { fetchPurchaseOrderCount } from "@/api/purchase_order.tsx"
import { UserState } from "@/auth/user"
import { accounting_pages_hidden } from "@/app/page_access"
import { SidebarExpandDisabledState } from "@/app/sidebar_prefs"
import { NotificationsSheet } from "@/components/notifications/NotificationsSheet"
import { useToastNotifications } from "@/components/ui/use-toast"
import { useOrgSettings } from "@/org_settings/state"
import { classes, isLauraChenelUser, isSaffronRoadUser } from "@/utils"
import {
  Bell,
  BookOpenCheck,
  BarChart3,
  Calendar,
  FileUp,
  Home,
  Inbox,
  LogOut,
  Pin,
  PinOff,
  Receipt,
  Rss,
  Settings,
  CircleHelp,
  Tag,
  TrendingUp,
  Wand2,
} from "lucide-react"
import { ComponentType } from "preact"
import { useRef, useState } from "preact/hooks"
import useSWR from "swr"
import { useLocation } from "wouter-preact"

const COLLAPSED_WIDTH = "3.5rem"
const EXPANDED_WIDTH = "15rem"
const HOVER_EXPAND_DELAY_MS = 150
const PINNED_STORAGE_KEY = "sidebar_pinned"

/** A parent item's link/highlight matches any location under its path (e.g. /deductions matches /deductions/won-pending-payment). */
export function is_item_active(location: string, href: string): boolean {
  return location.toLowerCase().includes(href.toLowerCase())
}

/** Subpage links highlight only on an exact path match so sibling subpages don't light up together. */
export function is_subitem_active(location: string, href: string): boolean {
  return location.toLowerCase().split("?")[0] === href.toLowerCase()
}

/** Preserve current query params (e.g. date filters) when navigating between pages. */
export function build_nav_href(href: string, location: string): string {
  const query_params = location.split("?")[1] || ""
  return query_params ? `${href}?${query_params}` : href
}

/** Whether the sidebar shows labels. The user's expand opt-out wins over both pinning and hover. */
export function is_sidebar_expanded(disable_expand: boolean, pinned: boolean, hovered: boolean): boolean {
  if (disable_expand) return false
  return pinned || hovered
}

interface SidebarSubItem {
  href: string
  label: string
}

interface SidebarItemProps {
  href: string
  icon: ComponentType<Omit<React.SVGProps<SVGSVGElement>, "size">> // Adjusted to omit 'size' prop
  label: string
  expanded: boolean
  sub_items?: SidebarSubItem[]
}

function SidebarItem({ href, icon: Icon, label, expanded, sub_items }: SidebarItemProps) {
  const [location] = useLocation()
  const isSelected = is_item_active(location, href)

  return (
    <div className="w-full">
      <a
        href={build_nav_href(href, location)}
        className={classes(
          "flex h-9 items-center w-full transition-colors",
          isSelected ? "bg-white/10 text-white" : "text-white/75 hover:text-white hover:bg-white/5"
        )}>
        {/* Fixed-width section for icon */}
        <div className="w-14 flex justify-center items-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <span
          className={classes(
            "whitespace-nowrap text-sm transition-opacity duration-150",
            expanded ? "opacity-100" : "opacity-0"
          )}>
          {label}
        </span>
      </a>
      {/* Sub items are always rendered at a fixed height so parent icons sit in the same spot
          whether or not the sidebar is expanded (no layout shift on hover-expand). Collapsed,
          each row is a small dot that still links to its subpage. */}
      {sub_items && (
        <div className="flex flex-col">
          {sub_items.map(sub => (
            <a
              key={sub.href}
              href={build_nav_href(sub.href, location)}
              title={expanded ? undefined : sub.label}
              className={classes(
                "flex h-7 items-center w-full transition-colors",
                is_subitem_active(location, sub.href)
                  ? "text-white bg-white/10"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              )}>
              {/* Same fixed-width icon column as parent rows, so dots align under the icon */}
              <div className="w-14 flex justify-center items-center shrink-0">
                <div className="h-1 w-1 rounded-full bg-current" />
              </div>
              <span
                className={classes(
                  "whitespace-nowrap text-[13px] transition-opacity duration-150",
                  expanded ? "opacity-100" : "opacity-0"
                )}>
                {sub.label}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

interface SidebarActionItemProps {
  onClick: () => void
  icon: ComponentType<Omit<React.SVGProps<SVGSVGElement>, "size">>
  label: string
  expanded: boolean
  /** Small count pinned to the icon, e.g. unread notifications. Hidden when zero. */
  badge?: number
}

/** A sidebar row that runs an action instead of navigating — same layout as `SidebarItem`. */
function SidebarActionItem({ onClick, icon: Icon, label, expanded, badge }: SidebarActionItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={expanded ? undefined : label}
      className="flex h-9 items-center w-full transition-colors text-white/75 hover:text-white hover:bg-white/5">
      <div className="w-14 flex justify-center items-center shrink-0 relative">
        <Icon className="h-5 w-5" />
        {badge ? (
          <span className="absolute top-1/2 left-1/2 -mt-4 ml-0.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center font-semibold">
            {badge > 99 ? "99+" : badge}
          </span>
        ) : null}
      </div>
      <span
        className={classes(
          "whitespace-nowrap text-sm transition-opacity duration-150",
          expanded ? "opacity-100" : "opacity-0"
        )}>
        {label}
      </span>
    </button>
  )
}

/** Renders a divider line when collapsed, and the section's name when expanded, at a constant height to avoid layout shift. */
function SidebarSection({ label, expanded }: { label: string; expanded: boolean }) {
  return (
    <div class="w-full h-4 flex items-center px-3">
      {expanded ? (
        <span class="text-[11px] tracking-wide text-white/40 whitespace-nowrap">{label}</span>
      ) : (
        <div class="w-full border-t border-white/20" />
      )}
    </div>
  )
}

export function Sidebar() {
  // Use simpler-state instead of local state
  let user = UserState.use(u => u!)
  const hide_accounting = accounting_pages_hidden(user, useOrgSettings())
  // Short pays only mean something for orgs with an accounting integration that has synced
  // invoices — there is nothing to compare payments against otherwise. The endpoint answers
  // `eligible: false` for anyone who can't see the screen, so no role check is needed here.
  const { data: short_pays } = useSWR("short-pays-summary", fetchShortPaySummary)
  // Only brands UNFI Canada sends receiving reports for have a fill rate to watch.
  const { data: inbound_fill_rate } = useSWR("inbound-fill-rate-sidebar", () => fetchInboundFillRate(4), { revalidateOnFocus: false })
  // Only brands whose Holland & Barrett Vendor Portal is synced have deliveries to show. A year, so
  // a brand between orders keeps the entry.
  const { data: hb_deliveries } = useSWR("hb-deliveries-sidebar", () => fetchDeliveries(365), { revalidateOnFocus: false })
  // Only brands whose EDI purchase orders reach a connected mailbox have orders to show.
  const { data: purchase_orders } = useSWR("purchase-orders-sidebar", fetchPurchaseOrderCount, { revalidateOnFocus: false })
  // Open Deductions only means something for orgs whose Sage integration parks deductions in a
  // holding account; everyone else keeps the classic code-then-apply flow and no menu entry.
  const { data: open_deductions } = useSWR("open-deductions-sidebar", async () => {
    const res = await listOpenDeductions()
    return res.ok ? res.value.data : null
  })
  // Some users dislike the expanding sidebar; this localStorage preference
  // (Settings → Preferences) keeps it a static rail.
  const expand_disabled = SidebarExpandDisabledState.use()
  const [pinned, setPinned] = useState(() => localStorage.getItem(PINNED_STORAGE_KEY) === "true")
  const [hovered, setHovered] = useState(false)
  const hover_timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [notifications_open, set_notifications_open] = useState(false)
  const { unread_count } = useToastNotifications()

  const expanded = is_sidebar_expanded(expand_disabled, pinned, hovered)

  function clear_hover_timer() {
    if (hover_timer.current) {
      clearTimeout(hover_timer.current)
      hover_timer.current = null
    }
  }

  function handle_mouse_enter() {
    if (expand_disabled) return
    clear_hover_timer()
    hover_timer.current = setTimeout(() => setHovered(true), HOVER_EXPAND_DELAY_MS)
  }

  function handle_mouse_leave() {
    clear_hover_timer()
    setHovered(false)
  }

  function handle_focus_out(event: FocusEvent) {
    const target = event.currentTarget as HTMLElement
    if (!target.contains(event.relatedTarget as Node | null)) {
      setHovered(false)
    }
  }

  function toggle_pinned() {
    const next = !pinned
    setPinned(next)
    localStorage.setItem(PINNED_STORAGE_KEY, String(next))
  }

  return (
    // The in-flow placeholder: reserves rail width normally, full width when pinned (pushes content).
    // z-40 goes here (not on the panel): position:sticky creates a stacking context, so a z-index
    // on the inner panel alone would be trapped inside it and page content would paint on top.
    <div class="shrink-0 h-screen sticky top-0 self-start z-40" style={{ width: pinned && !expand_disabled ? EXPANDED_WIDTH : COLLAPSED_WIDTH }}>
      {/* Scrolls on a short window, but with no visible scrollbar: a classic (always-shown) one takes
          its width out of the 56px rail and cuts into the icons. */}
      <div
        class="absolute inset-y-0 left-0 z-40 flex flex-col bg-primary border-r-2 border-[#2e2ef8] overflow-x-hidden overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden transition-[width] duration-200"
        style={{ width: expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH }}
        onMouseEnter={handle_mouse_enter}
        onMouseLeave={handle_mouse_leave}
        onFocusIn={() => !expand_disabled && setHovered(true)}
        onFocusOut={handle_focus_out}>
        <div className="flex flex-col items-start gap-4 sm:py-5 w-full">
          <div className="flex items-center w-full">
            {/* Always have the logo in the same position */}
            <div className="w-14 flex justify-center shrink-0">
              {user.role === "system_admin" ? (
                <a href="/select-org" title="Switch organization">
                  <img
                    class="h-8 w-8 cursor-pointer hover:opacity-80 transition-opacity"
                    src="/logo/rounded.png"
                    alt="MarginWiz"
                  />
                </a>
              ) : (
                <img class="h-8 w-8" src="/logo/rounded.png" alt="MarginWiz" />
              )}
            </div>
            <span
              className={classes(
                "text-white text-sm font-medium whitespace-nowrap transition-opacity duration-150",
                expanded ? "opacity-100" : "opacity-0"
              )}>
              MarginWiz
            </span>
            {expanded && (
              <button
                type="button"
                onClick={toggle_pinned}
                title={pinned ? "Unpin sidebar" : "Keep sidebar expanded"}
                className="ml-auto mr-3 text-white/50 hover:text-white transition-colors">
                {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
              </button>
            )}
          </div>
          {/* AI Wizard - system_admin only */}
          {user.role === "system_admin" && <SidebarItem href="/wizard" icon={Wand2} label="Wizard" expanded={expanded} />}

          {/* Brokers only get the promo calendar (the server enforces the same allowlist). */}
          {user.role !== "broker" && (
            <>
              {/* Deductions Domain */}
              <SidebarItem href="/dashboard" icon={Home} label="Dashboard" expanded={expanded} />
              <SidebarItem
                href="/deductions"
                icon={Tag}
                label="Deductions"
                expanded={expanded}
                sub_items={[
                  { href: "/deductions/won-pending-payment", label: "Won, pending payment" },
                  { href: "/spoilage", label: "Spoilage by item" },
                  // Short pays are system-admin only for now, and only for orgs with synced invoices.
                  ...(short_pays?.eligible ? [{ href: "/accounting/short-pays", label: "Short pays" }] : []),
                  ...(inbound_fill_rate?.dcs.length ? [{ href: "/fill-rate", label: "Fill rate" }] : []),
                  ...(hb_deliveries?.orders.length ? [{ href: "/deliveries", label: "H&B deliveries" }] : []),
                  ...(purchase_orders?.count ? [{ href: "/purchase-orders", label: "Purchase orders" }] : []),
                ]}
              />
              <SidebarItem href="/activities" icon={Rss} label="Activities" expanded={expanded} />

              {/* An org can keep these from its Sales team (Settings > Organization). */}
              {!hide_accounting && (
                <>
                  <SidebarSection label="Accounting" expanded={expanded} />
                  <SidebarItem href="/transactions" icon={BookOpenCheck} label="Transactions" expanded={expanded} />
                  <SidebarItem href="/accounting/checks" icon={Receipt} label="Cash application" expanded={expanded} />
                  {open_deductions?.holding_account && (
                    <SidebarItem href="/accounting/open-deductions" icon={Inbox} label="Open deductions" expanded={expanded} />
                  )}
                  <SidebarItem href="/file-imports" icon={FileUp} label="File imports" expanded={expanded} />
                </>
              )}
            </>
          )}

          <SidebarSection label="Trade promotion" expanded={expanded} />
          <SidebarItem
            href="/trade-promotion-management"
            icon={Calendar}
            label="Promotions"
            expanded={expanded}
            sub_items={[
              { href: "/trade-promotion-management", label: "Promo calendar" },
              { href: "/trade-promotion-management/create", label: "Create promotion" },
              { href: "/trade-promotion-management/accruals", label: "Promotion accruals" },
              { href: "/trade-promotion-management/accrual-report", label: "Month-end report" },
              { href: "/trade-promotion-management/accrual-bank", label: "Accrual bank" },
              ...(isLauraChenelUser(user.org_id) && user.role !== "broker" ? [{ href: "/trade-promotion-management/trade-review", label: "Trade review" }] : []),
              ...(isSaffronRoadUser(user.org_id) && user.role !== "broker" ? [{ href: "/trade-promotion-management/promo-lift-review", label: "Promo lift review" }] : []),
            ]}
          />
          {user.role !== "broker" && (
            <>
              <SidebarItem href="/sales-analysis" icon={BarChart3} label="Sales analysis" expanded={expanded} />
              <SidebarItem href="/sales-forecasting" icon={TrendingUp} label="Sales forecasting" expanded={expanded} />
            </>
          )}
        </div>
        <nav className="mt-auto flex flex-col items-start w-full gap-4 sm:py-5">
          <SidebarActionItem
            onClick={() => set_notifications_open(true)}
            icon={Bell}
            label="Notifications"
            expanded={expanded}
            badge={unread_count}
          />
          <NotificationsSheet open={notifications_open} onOpenChange={set_notifications_open} />
          <SidebarItem href="/help" icon={CircleHelp} label="Help" expanded={expanded} />
          {user.role !== "broker" && <SidebarItem href="/settings" icon={Settings} label="Settings" expanded={expanded} />}
          <SidebarItem href="/logout" icon={LogOut} label="Sign out" expanded={expanded} />
        </nav>
      </div>
    </div>
  )
}
